import { useCallback, useEffect, useState } from "react";
import styles from "./cashouts.module.scss";
import {
  BUTTON,
  FAILED_CASHOUTS_TAB,
  HEADER,
  IN_PROGRESS_CASHOUTS_TAB,
  MultiCashoutStatus,
  WAITING_CASHOUTS_TAB,
  TABLE_HEADERS,
} from "./constants";
import SearchInput from "../../components/common/searchInput";
import { Tab, Tabs } from "@mui/material";
import MultiCashoutsTableShimmer from "./MultiCashoutsTableShimmer";
import MultiCashoutsListItem from "./MultiCashoutsListItem";
import Paginations from "../../components/common/Pagination";
import {
  getCurrentMultiCashouts,
  getFailedMultiCashouts,
  getMultiCashoutCountByType,
  getWaitingMultiCashouts,
} from "../../setup/api/services/multiCashouts";
import {
  IMultiCashoutCountByTypeResponseData,
  MultiCashoutCountStatus,
  MultiCashoutItem,
  MultiCashoutPaymentItem,
} from "../../setup/api/services/multiCashouts/models";

const PAGE_SIZE = 10;

const MultiCashouts = () => {
  const [cashouts, setCashouts] = useState<
    (MultiCashoutItem | MultiCashoutPaymentItem)[]
  >([]);

  const [multiCashoutCounts, setMultiCashoutCounts] =
    useState<IMultiCashoutCountByTypeResponseData>();
  const [searchValue, setSearchValue] = useState("");
  const [transactionSearchValue, setTransactionSearchValue] = useState("");
  const [tabType, setTabType] = useState<MultiCashoutStatus>(
    MultiCashoutStatus.WAITING
  );
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchWaitingMultiCashouts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWaitingMultiCashouts({
        userId: searchValue ?? undefined,
        domainId: transactionSearchValue,
        page: page - 1,
        size: PAGE_SIZE,
      });
      setCashouts(response.data.content);
      setPagesCount(response.data.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setCashouts([]);
    }
  }, [searchValue, transactionSearchValue, page]);

  const fetchMultiCashoutPaymentsByStatus = useCallback(
    async (tabType: string) => {
      setLoading(true);
      try {
        const params = {
          page: page - 1,
          size: PAGE_SIZE,
          domainId: transactionSearchValue,
          beneficiaryId: searchValue,
        };
        let response;
        if (tabType === MultiCashoutStatus.IN_PROGRESS) {
          response = await getCurrentMultiCashouts(params);
        } else {
          response = await getFailedMultiCashouts(params);
        }
        setCashouts(response.data.content);
        setPagesCount(response.data.totalPages);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setCashouts([]);
      }
    },
    [transactionSearchValue, searchValue, page]
  );

  const fetchMultiCashoutCounts = useCallback(async () => {
    try {
      const response = await getMultiCashoutCountByType({
        domainId: transactionSearchValue,
        beneficiaryId: searchValue,
      });
      setMultiCashoutCounts(response.data);
    } catch (error) {
      setMultiCashoutCounts(undefined);
    }
  }, [transactionSearchValue, searchValue]);

  useEffect(() => {
    if (tabType === MultiCashoutStatus.WAITING) {
      fetchWaitingMultiCashouts();
    } else {
      fetchMultiCashoutPaymentsByStatus(tabType);
    }
    fetchMultiCashoutCounts();
  }, [
    tabType,
    searchValue,
    transactionSearchValue,
    page,
    fetchMultiCashoutCounts,
    fetchWaitingMultiCashouts,
    fetchMultiCashoutPaymentsByStatus,
  ]);

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: MultiCashoutStatus
  ) => {
    setTabType(newValue);
    setPage(1);
  };

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  const pendingMultiCashoutsCount =
    multiCashoutCounts?.counts[MultiCashoutCountStatus.STILL_PENDING];
  const failedMultiCashoutsCount =
    multiCashoutCounts?.counts[MultiCashoutCountStatus.FAILED];

  const getTitleCountLabel = useCallback((count: number | undefined) => {
    return count && count > 0 ? `(${count})` : "";
  }, []);

  return (
    <div className={styles["page_layout"]}>
      <h1>{HEADER}</h1>
      <div className={styles.header_container}>
        <div className={styles["right_section"]}>
          <Tabs
            style={{ alignItems: "center" }}
            className={styles.tab_wrapper}
            value={tabType}
            onChange={handleTabChange}>
            <Tab
              value={MultiCashoutStatus.WAITING}
              label={WAITING_CASHOUTS_TAB}
              sx={{
                paddingLeft: "1rem",
                paddingRight: "1rem",
                fontSize: { xs: "0.75em", md: "1em" },
              }}
            />
            <Tab
              value={MultiCashoutStatus.IN_PROGRESS}
              label={`${IN_PROGRESS_CASHOUTS_TAB} ${getTitleCountLabel(
                pendingMultiCashoutsCount
              )}`}
              sx={{
                paddingLeft: "1rem",
                paddingRight: "1rem",
                fontSize: { xs: "0.75em", md: "1em" },
              }}
            />
            <Tab
              value={MultiCashoutStatus.FAILED}
              label={`${FAILED_CASHOUTS_TAB} ${getTitleCountLabel(
                failedMultiCashoutsCount
              )}`}
              sx={{
                paddingLeft: "1rem",
                paddingRight: "1rem",
                fontSize: { xs: "0.75em", md: "1em" },
              }}
            />
          </Tabs>
        </div>
        <div className={styles["left_section"]}>
          <SearchInput
            dir="ltr"
            placeholder={BUTTON.SEARCH_USER_ID}
            value={searchValue}
            style={{ height: "35px", width: "252px" }}
            handleChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
          <SearchInput
            dir="ltr"
            placeholder={BUTTON.SEARCH_CONTRACT_ID}
            value={transactionSearchValue}
            style={{ height: "35px", width: "252px" }}
            handleChange={(event) => {
              setTransactionSearchValue(event.target.value);
            }}
          />
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr className={styles.table_header}>
            <th className={styles.hint}>{TABLE_HEADERS.USER_NAME}</th>
            <th className={styles.hint}>
              {tabType === MultiCashoutStatus.WAITING
                ? TABLE_HEADERS.WAITING_CASHOUTS_NUMBER
                : TABLE_HEADERS.CASHOUTS_NUMBER}
            </th>
            <th className={styles.hint}>{TABLE_HEADERS.TOTAL_AMOUNT}</th>
          </tr>
        </thead>
        {loading ? (
          <MultiCashoutsTableShimmer />
        ) : (
          <tbody className={styles.table_body}>
            {cashouts?.map((cashout, index) => (
              <MultiCashoutsListItem key={index} multiCashoutItem={cashout} />
            ))}
          </tbody>
        )}
      </table>

      <>
        <div className={cashouts?.length ? "paginatorContainer" : "hide"}>
          <Paginations
            pagesCount={pagesCount}
            length={pagesCount}
            page={page}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </>
    </div>
  );
};

export default MultiCashouts;
